<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BatchInvoiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoices: {
      type: Array,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItemFromInvoices = (...args) => {
    const [invoices, fields] = args;
    const fieldNames = _.map(fields, 'name');

    let result = {};

    console.log('getItemFromInvoices', fieldNames, fields, invoices);

    _.each(invoices, (invoice, i) => {
      if (i === 0) {
        result = _.pick(invoice, fieldNames);
      }

      for (const { name, defaultValue } of fields) {
        if (result[name] != invoice[name]) {
          result[name] = defaultValue;
          continue;
        }

        if (_.isUndefined(result[name])) {
          result[name] = defaultValue;
          continue;
        }

        if (result[name] != null) {
          result[name] = invoice[name];
        }
      }
    });

    result['$updates'] = _.reduce(fieldNames, (m, v) => _.set(m, v, false), {});

    console.log(result);

    return result;
  };
  const getPayload = (...args) => {
    const [invoices, item] = args;

    const updates = _.keys(_.pickBy(item.$updates, _.identity));

    if (!updates.length) {
      return;
    }

    const update = _.pick(item, updates);

    return {
      items: _.map(invoices, (invoice) => ({
        id: invoice.id,
        etag: invoice.etag,
        value: update,
      })),
    };
  };

  const isReadonly = ref(false);
  const editableFields = ref([
    {
      name: 'fulfillment_date',
    },
    {
      name: 'due_date',
    },
    {
      name: 'payment_method',
    },
    {
      name: 'currency',
    },
    {
      name: 'accounting_date',
    },
    {
      name: 'billing_period',
      defaultValue: {
        start: null,
        end: null,
      },
    },
    {
      name: 'notes',
    },
  ]);
  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BatchInvoiceEdit');
  const item = ref(null);
  const currencies = ref(null);
  const paymentMethods = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    isReadonly: isReadonly.value,
    editableFields: editableFields.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    currencies: currencies.value,
    paymentMethods: paymentMethods.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return getItemFromInvoices(props.invoices, $data.value.editableFields);
  };
  const $$load_currencies = async () => {
    return $app.value.repo.get('list_currency');
  };
  const $$load_paymentMethods = async () => {
    return $app.value.repo
      .get('list_payment_method')
      .then((data) =>
        _.reduce(data, (m, v, k) =>
          _.set(m, v.id, $fn.value.pgettext('PaymentMethod', v.name))
        )
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'isReadonly' },
      { name: 'editableFields' },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'paymentMethods' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      currencies: $$load_currencies,
      paymentMethods: $$load_paymentMethods,
    },
    ux: {},
    dataRefs: {
      isReadonly,
      editableFields,
      labelAlign,
      formId,
      formName,
      item,
      currencies,
      paymentMethods,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BatchInvoiceEditor|Title',
        'Batch Edit Invoices'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoices,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoices');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_vo6odfper_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_mr2fv38di_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_request_7isr9d7zl_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_invoice',
      data: getPayload(props.invoices, item.value),
    };
  };

  const act_7315092382398562_closeModal_vo6odfper =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_vo6odfper_getActionArgs,
    });
  const inst_evh_7315092382398562_close_vga5nmjk5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_vo6odfper,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_vga5nmjk5],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_floaazwp0 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_zlnh6hlpl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_floaazwp0,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_zlnh6hlpl],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_iu82jktdb =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_81ucekq58 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_iu82jktdb,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_81ucekq58],
    event: 'close',
  });
  const act_1040167445267876_closeModal_mr2fv38di =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_mr2fv38di_getActionArgs,
    });
  const inst_evh_1040167445267876_success_mgsywd2ke =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_mr2fv38di,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_7isr9d7zl = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_7isr9d7zl_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_mgsywd2ke],
  });
  const inst_evh_1040167445267876_submit_khcv9212e =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_7isr9d7zl,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_khcv9212e],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_nf9pueoaq =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_3d1ar5y3h =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_nf9pueoaq,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_3d1ar5y3h],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_vga5nmjk5,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_zlnh6hlpl,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_81ucekq58,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_mgsywd2ke,
    inst_evh_1040167445267876_submit_khcv9212e,
    save: inst_evh_1040167445267876_submit_khcv9212e,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_3d1ar5y3h,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_vga5nmjk5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_vga5nmjk5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_zlnh6hlpl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_zlnh6hlpl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_81ucekq58 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_81ucekq58.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_mgsywd2ke = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_mgsywd2ke.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_khcv9212e = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_khcv9212e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_3d1ar5y3h = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_3d1ar5y3h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BatchInvoiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="fulfillment_date"
          :label="gettext('Fulfillment Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="fulfillment_date_enabled"
              v-model="item.$updates.fulfillment_date"
              :value="item.$updates.fulfillment_date"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.fulfillment_date =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            name="fulfillment_date"
            v-model="item.fulfillment_date"
            :disabled="!item.$updates.fulfillment_date"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="due_date"
          :label="pgettext('Invoice', 'Due Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="due_date_enabled"
              v-model="item.$updates.due_date"
              :value="item.$updates.due_date"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.due_date =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            class="w-auto"
            name="due_date"
            v-model="item.due_date"
            :disabled="!item.$updates.due_date"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="payment_method"
          :label="pgettext('Invoice', 'Payment Method')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="payment_method_enabled"
              v-model="item.$updates.payment_method"
              :value="item.$updates.payment_method"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.payment_method =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormSelect
            name="payment_method"
            v-model="item.payment_method"
            :value="item.payment_method"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.payment_method =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="paymentMethods"
            :disabled="!item.$updates.payment_method"
          >
          </ItpFormSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="accounting_date"
          :label="pgettext('Invoice', 'Accounting Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="accounting_date_enabled"
              v-model="item.$updates.accounting_date"
              :value="item.$updates.accounting_date"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.accounting_date =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            name="accounting_date"
            v-model="item.accounting_date"
            :disabled="!item.$updates.accounting_date"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_period"
          :label="gettext('Billing Period')"
          contentClass="input-group"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          v-if="item.billing_period"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="billing_period_enabled"
              v-model="item.$updates.billing_period"
              :value="item.$updates.billing_period"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.billing_period =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <div class="row">
            <div class="col-4">
              <ItpFormDatetime
                name="billing_period_start"
                v-model="item.billing_period.start"
                :placeholder="pgettext('BillingPeriod', 'From')"
                :disabled="!item.$updates.billing_period"
              >
              </ItpFormDatetime>
            </div>
            <div class="col-4">
              <ItpFormDatetime
                name="billing_period_end"
                v-model="item.billing_period.end"
                :placeholder="pgettext('BillingPeriod', 'To')"
                :disabled="!item.$updates.billing_period"
              >
              </ItpFormDatetime>
            </div>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="currency"
          :label="pgettext('Invoice', 'Currency')"
          :label-cols-md="4"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="currency_enabled"
              v-model="item.$updates.currency"
              :value="item.$updates.currency"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.currency =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormSelect
            name="currency"
            v-model="item.currency"
            :value="item.currency"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.currency =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="currencies"
            valueField="code"
            textField="code"
            :disabled="!item.$updates.currency"
          >
          </ItpFormSelect>
        </ItpFormGroup>
        <hr class="my-4" />
        <ItpFormGroup
          name="notes"
          :label="pgettext('Invoice', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="notes_enabled"
              v-model="item.$updates.notes"
              :value="item.$updates.notes"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.$updates.notes =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :value="item.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.notes"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
